'use client';

import { useCallback, useMemo, useRef } from 'react';

import { sendApiRequest } from '../vercel/http';

import { FIELD_ERROR_PLACEHOLDER, FormFieldAsyncValidator, FormFieldValidator } from './validators';

type BodyExtras = Record<string, any>;

/**
 * Returns a debounced function that checks if a field is unique.
 */
export const useValidateUnique = (
  endpoint: string,
  options: {
    /** Duration in milliseconds to debounce the validation. */
    debounce?: number;
    /** Changes to this are ignored. */
    initBody?: () => {
      /** The body key that we should use for the internal value. */
      valueKey: string;
      /** Any extra body data, e.g. `{ pageId: number }` */
      extras?: BodyExtras;
    };
  } = {}
): FormFieldAsyncValidator<string> => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const body = useMemo(() => options.initBody?.(), []);
  const debounce = options.debounce || 500;

  const timeout = useRef<NodeJS.Timeout>(undefined);

  return useCallback(
    (value: string, initialValue: string) => {
      const validate = async () => {
        // Hit the provided endpoint to see if the value is unique.
        let requestUrl = `${endpoint}`;
        const requestParams = body?.valueKey
          ? {
              ...body.extras,
              [body.valueKey]: value
            }
          : body?.extras;
        if (requestParams) {
          // Append object as key/value pairs in search param of request:
          const searchParams = new URLSearchParams();
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, kvalue] of Object.entries(requestParams)) {
            searchParams.append(key, kvalue);
          }
          requestUrl = `${requestUrl}?${searchParams.toString()}`;
        }

        const response = await sendApiRequest(requestUrl);
        timeout.current = undefined;

        // Handle request errors by defaulting to "not unique".
        if (response.isError) {
          const message = response.message || `${FIELD_ERROR_PLACEHOLDER} is not available`;
          console.error(message);
          return message;
        }

        // Data field "exists" indicating whether the value is unique.
        const { exists } = response.data || {};
        if (exists) {
          return `${FIELD_ERROR_PLACEHOLDER} is not available`;
        }

        return null;
      };

      return new Promise<string | null>((resolve) => {
        clearTimeout(timeout.current);
        // Ignore unique validation for initial value.
        if (value === initialValue) {
          resolve(null);
          return;
        }
        timeout.current = setTimeout(async () => {
          const error = await validate();
          resolve(error);
        }, debounce);
      });
    },
    [body?.extras, body?.valueKey, debounce, endpoint]
  );
};

/**
 * Returns a debounced function that checks if a field is unique.
 *
 * Accepts a callback function that returns a boolean rather than an endpoint.
 */
export const useValidateUniqueCustom = (
  /** Should return `true` when passing validation (name is unique). */
  callback: (value: string, initialValue: string) => Promise<boolean>,
  options: {
    /** Duration in milliseconds to debounce the validation. */
    debounce?: number;
  } = {}
): FormFieldAsyncValidator<string> => {
  const debounce = options.debounce || 500;

  const timeout = useRef<NodeJS.Timeout>(undefined);

  return useCallback(
    (value: string, initialValue: string) => {
      const validate = async () => {
        let errorMessage: string | null = null;
        try {
          const isValid = await callback(value, initialValue);
          if (!isValid) {
            errorMessage ||= `${FIELD_ERROR_PLACEHOLDER} is not available`;
          }
        } catch (e) {
          errorMessage ||= `Something went wrong while checking uniqueness`;
          console.error('Failed to validate name uniqueness', e);
        }
        timeout.current = undefined;
        return errorMessage;
      };

      return new Promise<string | null>((resolve) => {
        clearTimeout(timeout.current);
        // Ignore unique validation for initial value.
        if (value === initialValue) {
          resolve(null);
          return;
        }
        timeout.current = setTimeout(async () => {
          const error = await validate();
          resolve(error);
        }, debounce);
      });
    },
    [callback, debounce]
  );
};

export const validateFutureDate: FormFieldValidator<Date | string | null> = (
  date: Date | string | null,
  _initialDate: Date | string | null
) => {
  const dateValue = typeof date === 'string' ? new Date(date) : date;
  if (!dateValue) {
    return null;
  }
  if (dateValue.getTime() < new Date().getTime()) {
    return `Date must not be in the past`;
  }
  return null;
};
